// «База знаний» (R-U16, R-M2, um/2026-10-30/03 п.2): the core's «list | reading» as the mail — the articles of the section chosen in the
// left column (KnowledgeNav) | the article being read and edited. The open article is in the address: a link opens it directly.

import { useMemo } from 'react';
import { canWrite, type Id } from '@confdeck/core-shared';
import { ListBody, ListEmpty, ListHead, ListReader, ListRow, RecordView, TopBar, createRecord, useScreenSearch, navigate, openCard, registerSectionNav, useAccess, useLocation, useNarrow, useMeta, useQuery, useSync } from '@confdeck/core-web';
import { KnowledgeNav, useKnowledgeTitle, useSection } from './KnowledgeNav.tsx';
import { NO_SECTION, SORT, TAXONOMY_KEY, TYPE_KEY, articleHref, articleToOpen, orderOf, sectionOf } from './sections.ts';

registerSectionNav('knowledge', { nav: KnowledgeNav, useTitle: useKnowledgeTitle });

export function KnowledgeScreen() {
  const client = useSync();
  const meta = useMeta();
  const access = useAccess();
  const type = meta.typeByKey(TYPE_KEY);
  const taxonomy = meta.taxonomies().find((t) => t.key === TAXONOMY_KEY);
  const sectionId = useSection();
  const narrow = useNarrow();
  const q = useQuery(type?.id ?? '', null, SORT, '');

  // Articles of the section: by «Порядок», then by title (the query's sort).
  const articles = useMemo(() => {
    const out: { id: Id; title: string; order: number | null }[] = [];
    for (const id of q.ids) {
      const rec = client.store.record(id);
      if (!rec || sectionOf(rec) !== sectionId) continue;
      out.push({ id, title: String(rec.data.title ?? ''), order: orderOf(rec) });
    }
    return out;
  }, [q.ids, sectionId, client, meta]);
  const articleId = articleToOpen(useLocation().search.get('article'), articles.map((a) => a.id), narrow);
  const show = (id: string) => navigate(articleHref(sectionId, id), { replace: true });
  // The global search looks here first (R-U23): the articles of the open section by their title.
  useScreenSearch((query) => {
    const needle = query.trim().toLowerCase();
    return articles.filter((a) => a.title.toLowerCase().includes(needle))
      .map((a) => ({ id: a.id, recordId: a.id, label: a.title || 'Без названия', hint: sectionId === NO_SECTION ? 'Без раздела' : meta.term(sectionId as Id)?.name, icon: 'book', run: () => show(a.id) }));
  });

  const create = () => {
    if (!type) return;
    const data: Record<string, unknown> = { title: 'Без названия' };
    if (sectionId !== NO_SECTION) data.section = sectionId;
    show(createRecord(client, type.id, data));
  };

  if (!type || !taxonomy) return <ListEmpty icon="book" text="Раздел «База знаний» не найден" />;
  const writable = canWrite(access, type.id);
  const sectionName = sectionId === NO_SECTION ? 'Без раздела' : meta.term(sectionId as Id)?.name ?? '';

  return (
    <ListReader reading={!!articleId} onBack={() => show('')} listLabel="Статьи раздела" readerLabel="Статья"
      list={<>
        <ListHead title="Статьи" note={sectionName}
          add={writable ? { label: 'Статья', onClick: create } : undefined} />
        {articles.length ? (
          <ListBody label="Статьи">
            {articles.map((a) => <ListRow key={a.id} selected={a.id === articleId} onClick={() => show(a.id)} title={a.title || 'Без названия'} sub={a.order !== null ? `Порядок ${a.order}` : undefined} />)}
          </ListBody>
        ) : <ListEmpty icon="book" text="В этом разделе пока нет статей" />}
      </>}
      reader={articleId
        ? <RecordView key={articleId} id={articleId} embedded={{ onExpand: () => navigate(`/r/${articleId}`) }} onClose={() => show('')} onOpen={openCard} />
        : <><TopBar /><ListEmpty icon="book" text="Выберите статью" /></>} />
  );
}
